import { useEffect } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { shouldRecordErrorReplay } from "../utils/errorReplayUtils";
import { getLastUserAction } from "../utils/userActionTracker";
import { markSessionReplay } from "../utils/replayMonitoring";

interface UseDatadogRumProps {
  appVersion: string;
  environment: string;
}

// Track initialization status outside the hook to ensure singleton behavior
let isInitialized = false;

const useDatadogRum = ({ appVersion, environment }: UseDatadogRumProps) => {
  useEffect(() => {
    // Skip if already initialized or attempted
    if (isInitialized || datadogRum.getInitConfiguration()) {
      console.log("Datadog RUM already initialized, skipping.");
      isInitialized = true;
      return;
    }

    if (environment === "development" || environment === "local") {
      console.log(
        "Datadog RUM is disabled in development or local environment."
      );
      return;
    }

    datadogRum.init({
      applicationId: "ad2214ca-7d9d-4e5d-82c5-dfa7af0d6693",
      clientToken: "pubaafb5d055ecc2adf8587cd606181c44a",
      site: "us5.datadoghq.com",
      service: "futurhealth-plus",
      env: environment,
      version: appVersion,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 5,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
      beforeSend: (event) => {
        // Filter out noise from resource tracking
        if (event.type === "resource") {
          const url = event.resource?.url;
          // Guard against missing URL
          if (!url) return true;

          // Exclude analytics, ads, and other noise using more specific patterns
          const excludePatterns = [
            "googletagmanager",
            "google-analytics",
            "facebook.com",
            "analytics",
            "doubleclick",
            "/ads/", // Path segment match
            ".ads.", // Subdomain match
            "googlesyndication",
            "adservice",
            "hotjar",
          ];

          if (excludePatterns.some((pattern) => url.includes(pattern))) {
            return false;
          }
        }
        return true;
      },
    });

    // Note: PostHog IDs are added to Datadog global context from posthogService.ts
    // after PostHog initialization completes (in the loaded callback)

    // Datadog RUM uses global context properties until they are cleared.
    // Shared by error + unhandledrejection handlers so API guard logic stays in one place.
    const removeGlobalContextProperty = (key: string) => {
      const dd = datadogRum as unknown as {
        removeGlobalContextProperty?: (k: string) => void;
      };
      const fn = dd.removeGlobalContextProperty;
      if (typeof fn === "function") {
        fn.call(datadogRum, key);
      }
    };

    // Set up global error handlers for error-triggered replay with deduplication and filtering
    const handleError = (event: ErrorEvent) => {
      const error = event.error || new Error(event.message);

      // Get last user action for context
      const lastUserAction = getLastUserAction();
      const userActionKey = `${lastUserAction.type}_${lastUserAction.target}`;

      // Check if we should trigger replay (filtering + deduplication with user action context)
      const { shouldReplay, occurrenceCount } = shouldRecordErrorReplay(
        error,
        event.error?.stack,
        userActionKey
      );
      const isFiltered = !shouldReplay && occurrenceCount === 0;
      const isDuplicate = !shouldReplay && !isFiltered;

      let didSetLastUserAction = false;
      let didSetOccurrenceCount = false;

      try {
        if (!isFiltered) {
          datadogRum.addError(error, {
            error_origin: "window_error",
            last_user_action: lastUserAction,
            occurrence_count: occurrenceCount,
            is_duplicate: isDuplicate,
            is_filtered: isFiltered,
          });
        }

        if (shouldReplay) {
          // Add last user action to global context for debugging
          datadogRum.setGlobalContextProperty(
            "last_user_action",
            lastUserAction
          );
          didSetLastUserAction = true;

          // Start replay (startSessionReplayRecording is idempotent)
          datadogRum.startSessionReplayRecording();
          markSessionReplay(); // Track that this session has replay
        }

        // Add occurrence count to context (even if not triggering replay)
        if (occurrenceCount > 0) {
          datadogRum.setGlobalContextProperty(
            "error_occurrence_count",
            occurrenceCount
          );
          didSetOccurrenceCount = true;
        }
        // Datadog RUM automatically captures this error - no need to manually add it
      } finally {
        // Remove on microtask boundary so Datadog has a chance to associate context
        // with the error event before we clear it.
        queueMicrotask(() => {
          if (didSetLastUserAction)
            removeGlobalContextProperty("last_user_action");
          if (didSetOccurrenceCount)
            removeGlobalContextProperty("error_occurrence_count");
        });
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error =
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason));

      // Get last user action for context
      const lastUserAction = getLastUserAction();
      const userActionKey = `${lastUserAction.type}_${lastUserAction.target}`;

      // Check if we should trigger replay (filtering + deduplication with user action context)
      const { shouldReplay, occurrenceCount } = shouldRecordErrorReplay(
        error,
        undefined,
        userActionKey
      );
      const isFiltered = !shouldReplay && occurrenceCount === 0;
      const isDuplicate = !shouldReplay && !isFiltered;

      let didSetLastUserAction = false;
      let didSetOccurrenceCount = false;

      try {
        if (!isFiltered) {
          datadogRum.addError(error, {
            error_origin: "unhandled_rejection",
            last_user_action: lastUserAction,
            occurrence_count: occurrenceCount,
            is_duplicate: isDuplicate,
            is_filtered: isFiltered,
          });
        }

        if (shouldReplay) {
          // Add last user action to global context for debugging
          datadogRum.setGlobalContextProperty(
            "last_user_action",
            lastUserAction
          );
          didSetLastUserAction = true;

          // Start replay (startSessionReplayRecording is idempotent)
          datadogRum.startSessionReplayRecording();
          markSessionReplay(); // Track that this session has replay
        }

        // Add occurrence count to context (even if not triggering replay)
        if (occurrenceCount > 0) {
          datadogRum.setGlobalContextProperty(
            "error_occurrence_count",
            occurrenceCount
          );
          didSetOccurrenceCount = true;
        }
        // Datadog RUM automatically captures this rejection - no need to manually add it
      } finally {
        queueMicrotask(() => {
          if (didSetLastUserAction)
            removeGlobalContextProperty("last_user_action");
          if (didSetOccurrenceCount)
            removeGlobalContextProperty("error_occurrence_count");
        });
      }
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    isInitialized = true;

    console.log(
      `Datadog RUM initialized for environment: ${environment}, version: ${appVersion}`
    );

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, [appVersion, environment]);

  return datadogRum;
};

export default useDatadogRum;
